import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatPrice(price) {
  return new Intl.NumberFormat("en-US").format(price);
}

export function formatNumber(num) {
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000)?.toFixed(2) + "B";
  } else if (num >= 1_000_000) {
    return (num / 1_000_000)?.toFixed(2) + "M";
  } else if (num >= 1_000) {
    return (num / 1_000)?.toFixed(2) + "K";
  } else {
    return toCommaForm(num);
  }
}

export function toCommaForm(input) {
  if (input > 0) {
    const str = String(input);
    const parts = str.split(".");
    let integerPart = parts[0];
    const decimalPart = parts[1] || "";
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
  } else {
    return 0;
  }
}

export function formatNumberInput(input) {
  const str = String(input);

  let cleaned = "";
  let seenDot = false;
  for (const ch of str) {
    if (/[0-9]/.test(ch)) {
      cleaned += ch;
    } else if (ch === "." && !seenDot) {
      seenDot = true;
      cleaned += ".";
    }
  }

  let [intPart, decPart = ""] = cleaned.split(".");

  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const endsWithDot = seenDot && cleaned.endsWith(".");
  let formatted;
  if (endsWithDot) {
    formatted = withCommas + ".";
  } else if (decPart) {
    formatted = `${withCommas}.${decPart}`;
  } else {
    formatted = withCommas;
  }

  return formatted;
}

export function convertIsoToEst(isoString) {
  if (!isoString || typeof isoString !== "string") {
    throw new Error("Invalid ISO string: Input must be a non-empty string");
  }

  const date = new Date(isoString);
  if (isNaN(date.getTime())) {
    throw new Error("Invalid ISO string: Could not parse to valid Date");
  }

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    month: "short", // e.g., "Jul"
    day: "numeric", // e.g., "4"
    year: "numeric", // e.g., "2024"
    hour: "numeric", // e.g., "2"
    minute: "2-digit", // e.g., "09"
    hour12: true, // Use 12-hour format with AM/PM
  });

  // Get formatted parts
  const parts = formatter.formatToParts(date);
  const month = parts.find((p) => p.type === "month").value; // "Jul"
  const day = parseInt(
    parts.find((p) => p.type === "day").value,
    10,
  ).toString();
  const year = parts.find((p) => p.type === "year").value; // "2024"
  const hour = parts.find((p) => p.type === "hour").value; // "2"
  const minute = parts.find((p) => p.type === "minute").value; // "09"
  const period = parts.find((p) => p.type === "dayPeriod").value; // "PM"

  const stdFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    timeZoneName: "short",
  });

  const timeZoneShort = stdFormatter
    .formatToParts(date)
    .find((p) => p.type === "timeZoneName").value; // "EST" or "EDT"

  return `${month} ${day}, ${year}, ${hour}:${minute} ${period} ${timeZoneShort}`;
}

// A helper function to format the date
export const formatDate = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  // 'en-GB' locale to get day-month-year order. You can use 'en-US' too.
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC", // Important to handle the 'Z' correctly
  }).format(date);
};


export function wrapTextAroundImage(html) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  // Find <p> that contains <img>
  const paragraphs = doc.querySelectorAll("p:has(img)");

  paragraphs.forEach((p) => {
    const hasText = [...p.childNodes].some(
      (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== ""
    );

    if (hasText) {
      // Only restructure if both <img> and text exist
      const wrapperDiv = doc.createElement("div");

      [...p.childNodes].forEach((node) => {
        if (node.nodeName.toLowerCase() === "img") {
          // Wrap <img> in <div>
          const imgWrapper = doc.createElement("div");
          imgWrapper.appendChild(node.cloneNode(true));
          wrapperDiv.appendChild(imgWrapper);
        } else if (
          node.nodeType === Node.TEXT_NODE &&
          node.textContent.trim() !== ""
        ) {
          // Wrap text in <p>
          const textWrapper = doc.createElement("p");
          textWrapper.textContent = node.textContent.trim();
          wrapperDiv.appendChild(textWrapper);
        }
      });

      p.replaceWith(wrapperDiv);
    }
    // else: if <p> only contains <img>, do nothing
  });

  return doc.body.innerHTML;
}


export function convertEmptyLinksToSpans(html) {
  // Return an empty string if the input is null or undefined to prevent errors.
  if (!html) {
    return "";
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  // Find all anchor tags (<a>) in the document.
  // Using an array copy to safely modify the list during iteration.
  const anchorTags = Array.from(doc.querySelectorAll("a"));

  anchorTags.forEach((anchor) => {
    const href = anchor.getAttribute("href");
    
    // Check if the href attribute does not exist OR if it's an empty string.
    if (href === null || href.trim() === "") {
      // 1. Create a new <span> element.
      const span = doc.createElement("span");

      // 2. Move all child nodes (like text, <b>, <img>) from the <a> to the new <span>.
      while (anchor.firstChild) {
        span.appendChild(anchor.firstChild);
      }
      
      // 3. Copy all attributes from the <a> to the <span>, except for 'href'.
      for (const attr of anchor.attributes) {
        if (attr.name.toLowerCase() !== 'href') {
          span.setAttribute(attr.name, attr.value);
        }
      }

      // 4. Replace the original <a> tag with the new <span> tag.
      anchor.replaceWith(span);
    }
  });

  // Return the modified HTML content from the body.
  return doc.body.innerHTML;
}

export function processLinks(html) {
  // Return an empty string if the input is null or undefined to prevent errors.
  if (!html) {
    return "";
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  const anchorTags = Array.from(doc.querySelectorAll("a"));

  anchorTags.forEach((anchor) => {
    const href = anchor.getAttribute("href");
    
    // Condition 1: Check for an empty or missing href. (This part works)
    if (href === null || href.trim() === "") {
      const span = doc.createElement("span");
      while (anchor.firstChild) {
        span.appendChild(anchor.firstChild);
      }
      for (const attr of anchor.attributes) {
        if (attr.name.toLowerCase() !== 'href') {
          span.setAttribute(attr.name, attr.value);
        }
      }
      anchor.replaceWith(span);

    } else {
      // Condition 2: Check for a valid href and an image inside.
      const image = anchor.querySelector("img");
      
      if (image) {
        // --- Apply Promotion Styling ---
        anchor.style.position = 'relative';
        anchor.style.display = 'inline-block';
        anchor.style.border = '1px solid purple';
        anchor.style.borderRadius = '5px';
        anchor.style.textDecoration = 'none';

        // ✅ THE FIX: Use a <span> instead of a <div> for the badge.
        const promoTag = doc.createElement("span"); 
        promoTag.textContent = "Promotion";

        // All the styles below work perfectly on a <span> when it's absolutely positioned.
        promoTag.style.position = 'absolute';
        promoTag.style.top = '10px';
        promoTag.style.right = '10px';
        promoTag.style.backgroundColor = 'purple';
        promoTag.style.color = 'white';
        promoTag.style.padding = '2px 8px';
        promoTag.style.borderRadius = '5px';
        promoTag.style.fontSize = '12px';
        promoTag.style.fontWeight = 'bold';
        promoTag.style.zIndex = '10';

        anchor.appendChild(promoTag);
      }
    }
  });

  return doc.body.innerHTML;
}

